import { Button } from '@/components/ui/button'
import { ErrorState } from '@/components/ui/error-state'
import { branding } from '@/config/branding'
import { getDefaultRouteForUser, useRouteSegments } from '@/lib/routerUtils'
import {
	ACCESS_DENIED_NO_PROJECT_ACCESS_REASON,
	ACCESS_DENIED_ORG_OWNER_ONLY_REASON,
	ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON,
	accessDeniedSearchSchema,
} from '@/lib/searchSchemas'
import { useAuthStore } from '@/stores/authStore'
import { Link, useSearch } from '@tanstack/react-router'
import { Home, LogOut, ShieldX } from 'lucide-react'

export function AccessDeniedPage() {
	const logout = useAuthStore(state => state.logout)
	const parsed = accessDeniedSearchSchema.safeParse(useSearch({ strict: false }))
	const reason = parsed.success ? parsed.data.reason : undefined
	const params = useRouteSegments()
	const isOrgOwnerOnlyBlock = reason === ACCESS_DENIED_ORG_OWNER_ONLY_REASON
	const isNoProjectAccess = reason === ACCESS_DENIED_NO_PROJECT_ACCESS_REASON
	const isProjectSettingsAdminOnly = reason === ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON

	const homePath = params.orgId && params.tenantId ? getDefaultRouteForUser(params.tenantId, params.orgId) : null

	const title = 'Access Denied'
	const message = isNoProjectAccess
		? "You don't have access to any projects yet. Contact your administrator to be added to a project."
		: isOrgOwnerOnlyBlock
			? 'Only owners can access organization-wide sections. Contact an organization owner if you need this access.'
			: isProjectSettingsAdminOnly
				? 'Only project administrators can access Project Settings. Contact your administrator if you need this access.'
				: "You don't have permission to access any pages in this project. Please contact your administrator to request access."

	return (
		<ErrorState
			title={title}
			message={message}
			showSupportEmail={false}
			containerClassName='min-h-screen w-full overflow-hidden'
			icon={ShieldX}
			tone='destructive'
			actions={
				<>
					{homePath ? (
						<Button asChild className='text-control'>
							<Link to={homePath}>
								<Home className='size-(--icon-standalone)' />
								Go to home
							</Link>
						</Button>
					) : null}
					<Button onClick={logout} variant='outline' className='text-control'>
						<LogOut className='size-(--icon-standalone)' />
						Sign Out
					</Button>
					{isProjectSettingsAdminOnly ? null : (
						<Button
							data-testid='access-denied-support'
							onClick={() => {
								window.location.href = `mailto:${branding.supportEmail}`
							}}
							className='text-control'>
							Contact Support
						</Button>
					)}
				</>
			}
		/>
	)
}
